// src/components/AllJobsView.tsx
import React, { useState } from 'react';
import { Archive, Printer, Trash2, ClipboardCopy, ArrowUpDown, User, Euro, Link, CreditCard, Loader2, Pencil } from 'lucide-react';
import { formatDateEU } from '../utils/formatters';

interface AllJobsViewProps {
    jobs: any[];
    customers: any[];
    searchQuery: string;
    
    // Functies
    getCustomerName: (id: string) => string;
    getRacketName: (job: any) => string;
    openCustomerHistory: (customer: any) => void;
    togglePaid: (job: any) => void;
    openPrintModal: (job: any) => void;
    openJobEditModal: (job: any) => void;
    
    // Selectie logica
    selectedJobIds: Set<string>;
    toggleJobSelection: (id: string) => void;
    handleSelectAllJobs: (filteredJobs: any[]) => void;
    handleBulkPrintJobs: () => void;
    handleBulkDeleteJobs: () => void;
    
    // Import logica
    setImportText: (val: string) => void;
    setShowJobImportModal: (val: boolean) => void;
    
    // Mollie
    generateMollieLink: (job: any) => void;
    setPaymentModalJob: (job: any) => void;
    generatingMollieId: string | null;
}

export default function AllJobsView({
    jobs, customers, searchQuery,
    getCustomerName, getRacketName, openCustomerHistory, togglePaid, openPrintModal, openJobEditModal,
    selectedJobIds, toggleJobSelection, handleSelectAllJobs, handleBulkPrintJobs, handleBulkDeleteJobs,
    setImportText, setShowJobImportModal,
    generateMollieLink, setPaymentModalJob, generatingMollieId
}: AllJobsViewProps) {

    // --- LOKALE STATE ---
    const [archiveSortConfig, setArchiveSortConfig] = useState({ key: 'date', direction: 'descending' });

    // --- LOKALE FUNCTIE ---
    const requestArchiveSort = (key: string) => {
        let direction = 'ascending';
        if (archiveSortConfig.key === key && archiveSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setArchiveSortConfig({ key, direction });
    };

    // --- LOGICA ---
    let filteredJobs = jobs.filter(job => getCustomerName(job.customerId).toLowerCase().includes(searchQuery.toLowerCase()));
      
    filteredJobs.sort((a, b) => {
        let valA, valB;
        if (archiveSortConfig.key === 'customerName') {
            valA = getCustomerName(a.customerId).toLowerCase();
            valB = getCustomerName(b.customerId).toLowerCase();
        } else if (archiveSortConfig.key === 'racket') {
            valA = getRacketName(a).toLowerCase();
            valB = getRacketName(b).toLowerCase();
        } else if (archiveSortConfig.key === 'date') {
            valA = new Date(a.date).getTime() || 0;
            valB = new Date(b.date).getTime() || 0;
        } else {
            valA = (a[archiveSortConfig.key] || '').toString().toLowerCase();
            valB = (b[archiveSortConfig.key] || '').toString().toLowerCase();
        }

        if (valA < valB) return archiveSortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return archiveSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });

    const allSelected = filteredJobs.length > 0 && filteredJobs.every(j => selectedJobIds.has(j.id));

    // --- WEERGAVE ---
    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center"><Archive className="w-6 h-6 mr-2 text-indigo-600 dark:text-indigo-400" />Archief <span className="ml-2 text-slate-400 text-sm font-normal">({filteredJobs.length})</span></h2>
                <div className="flex gap-2">
                    {selectedJobIds.size > 0 && (
                        <>
                            <button 
                                onClick={handleBulkPrintJobs}
                                className="flex items-center px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
                            >
                                <Printer className="w-4 h-4 mr-2" />
                                Printen ({selectedJobIds.size})
                            </button>
                            <button 
                                onClick={handleBulkDeleteJobs}
                                className="flex items-center px-4 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijder ({selectedJobIds.size})
                            </button>
                        </>
                    )}
                    <button onClick={() => { setImportText(''); setShowJobImportModal(true); }} className="flex items-center px-4 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800"><ClipboardCopy className="w-4 h-4 mr-2" />Importeer Archief</button>
                </div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700"><h3 className="font-bold text-slate-800 dark:text-white">Alle Opdrachten (Archief)</h3></div>
                <div className="overflow-x-auto">
                    {filteredJobs.length > 0 ? (
                        <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                            <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-900/50">
                                <tr>
                                    <th className="px-4 py-3 w-10">
                                        <input 
                                            type="checkbox" 
                                            checked={allSelected} 
                                            onChange={() => handleSelectAllJobs(filteredJobs)}
                                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                                        />
                                    </th>
                                    <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestArchiveSort('date')}>Datum <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                    <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestArchiveSort('customerName')}>Klant <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                    <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestArchiveSort('racket')}>Racket/Snaar/Spanning <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                    <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestArchiveSort('status')}>Status <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                    <th className="px-6 py-3 text-right">Actie</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {filteredJobs.map(job => (
                                    <tr key={job.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                        <td className="px-4 py-4">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedJobIds.has(job.id)} 
                                                onChange={() => toggleJobSelection(job.id)}
                                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                                            />
                                        </td>
                                        <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">{formatDateEU(job.date)}</td>
                                        <td className="px-6 py-4">
                                            <div 
                                                className="font-medium text-slate-900 dark:text-white cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors inline-block"
                                                onClick={() => { const c = customers.find(cust => cust.id === job.customerId); if (c) openCustomerHistory(c); }}
                                            >
                                                {getCustomerName(job.customerId)}
                                            </div>
                                            <div className="mt-1">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800/50">
                                                    <User className="w-3 h-3 mr-1" />
                                                    {job.stringer ? job.stringer.split(' ')[0] : 'Niemand'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">
                                            <div className="font-medium text-slate-700 dark:text-slate-300">{getRacketName(job)}</div>
                                            <div>{job.string}</div>
                                            <div>{job.tensionMain}/{job.tensionCross} {job.tensionUnit || 'kg'}</div>
                                        </td>
                                        <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${job.status === 'Klaar' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>{job.status}</span></td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => togglePaid(job)} className={`p-2 rounded-full ${job.paid ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-300 dark:bg-slate-700 dark:text-slate-400'}`} title="Markeer als betaald"><Euro className="w-4 h-4" /></button>
                                                
                                                <button 
                                                    onClick={() => job.paymentLink ? setPaymentModalJob(job) : generateMollieLink(job)} 
                                                    className={`p-2 rounded-full ${job.paymentLink ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-blue-100 hover:text-blue-600'}`} 
                                                    title={job.paymentLink ? "Bekijk Betaalverzoek" : "Genereer Mollie Betaallink"}
                                                >
                                                    {generatingMollieId === job.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (job.paymentLink ? <Link className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />)}
                                                </button>

                                                <button onClick={() => openPrintModal(job)} className="p-2 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-indigo-100 hover:text-indigo-600" title="Print Sticker"><Printer className="w-4 h-4" /></button>
                                                <button onClick={() => openJobEditModal(job)} className="p-2 rounded-full text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/50" title="Bewerken"><Pencil className="w-4 h-4" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <div className="p-8 text-center">Geen historie gevonden.</div>}
                </div>
            </div>
        </div>
      );
}